import { useState, type ReactNode } from 'react';
import { TrashIcon } from '@phosphor-icons/react';
import { T, Var, useGT } from 'gt-react';
import type { WorkbookDefinition } from '@erp/shared-types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { WorkbookRowMenu } from './WorkbookRowMenu';
import { workbookSheetDependents } from './workbookSheetDependents';

/**
 * Delete one Worksheet or pivot, after a confirmation that lists what reads
 * it. Deleting is a Workbook edit, so undo brings it back. Shown on hover or
 * keyboard focus of its row, and while open, laid over the row's end.
 */
export function WorkbookSheetMenu({
  sheet,
  definition,
  disabled,
  onDelete,
}: {
  sheet: { id: string; name: string };
  definition: WorkbookDefinition;
  disabled: boolean;
  onDelete: () => void;
}) {
  const gt = useGT();
  const [confirming, setConfirming] = useState(false);
  const optionsLabel = gt('{name} options', {
    context: 'workbook worksheet menu',
    name: sheet.name,
  });
  const dependents = confirming
    ? workbookSheetDependents(definition, sheet.id)
    : { sheets: [], outputs: [] };
  return (
    <>
      <WorkbookRowMenu label={optionsLabel} disabled={disabled}>
        <DropdownMenuItem
          variant="destructive"
          onClick={() => setConfirming(true)}
        >
          <TrashIcon />
          <T context="workbook worksheet menu">Delete</T>
        </DropdownMenuItem>
      </WorkbookRowMenu>
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <T context="workbook delete worksheet title">
                Delete <Var>{sheet.name}</Var>?
              </T>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <T context="workbook delete worksheet description">
                You can undo this.
              </T>
            </AlertDialogDescription>
          </AlertDialogHeader>
          {dependents.sheets.length > 0 && (
            <DependentList
              heading={
                <T context="workbook delete worksheet: worksheets reading it">
                  These worksheets read it and will need another input:
                </T>
              }
              items={dependents.sheets}
            />
          )}
          {dependents.outputs.length > 0 && (
            <DependentList
              heading={
                <T context="workbook delete worksheet: outputs using it">
                  These outputs use it and will lose its data:
                </T>
              }
              items={dependents.outputs}
            />
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>
              <T context="dialog cancel button">Cancel</T>
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={onDelete}
            >
              <T context="workbook delete worksheet action">Delete</T>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function DependentList({
  heading,
  items,
}: {
  heading: ReactNode;
  items: readonly { id: string; name: string }[];
}) {
  return (
    <div className="space-y-1 text-sm">
      <p className="text-muted-foreground">{heading}</p>
      <ul className="list-disc pl-5">
        {items.map((item) => (
          <li key={item.id}>{item.name}</li>
        ))}
      </ul>
    </div>
  );
}
